import { useCallback, useEffect, useRef, useState } from "react";

// The card's exit animation runs this long before the list is fetched again.
const EXIT_MS = 350;

export interface CardExit {
  processingIds: ReadonlySet<number>;
  exitingIds: ReadonlySet<number>;
  /**
   * Send one answer that takes the card off the deck. The card starts its
   * exit at once; on success the list is fetched again after the animation,
   * on refusal the card comes back and `onRefused` receives the error. A
   * second answer for a card already in flight is ignored.
   */
  runExit: (
    itemId: number,
    send: () => Promise<unknown>,
    onRefused: (err: unknown) => void,
  ) => Promise<void>;
}

function without(set: ReadonlySet<number>, id: number): Set<number> {
  const next = new Set(set);
  next.delete(id);
  return next;
}

/** The deck's in-flight and leaving cards (confirm, correct and the fence's answers). */
export function useCardExit(onUpdated: () => void): CardExit {
  const [processingIds, setProcessingIds] = useState<ReadonlySet<number>>(new Set());
  const [exitingIds, setExitingIds] = useState<ReadonlySet<number>>(new Set());
  // The pending reloads after an exit; cleared when the deck unmounts, so a
  // timer never reloads a list that is no longer on screen.
  const timers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  const runExit = useCallback(
    async (itemId: number, send: () => Promise<unknown>, onRefused: (err: unknown) => void) => {
      if (processingIds.has(itemId)) return;
      setProcessingIds((prev) => new Set(prev).add(itemId));
      setExitingIds((prev) => new Set(prev).add(itemId));
      try {
        await send();
        const timer = setTimeout(() => {
          timers.current.delete(timer);
          onUpdated();
          setExitingIds((prev) => without(prev, itemId));
        }, EXIT_MS);
        timers.current.add(timer);
      } catch (err) {
        setExitingIds((prev) => without(prev, itemId));
        onRefused(err);
      } finally {
        setProcessingIds((prev) => without(prev, itemId));
      }
    },
    [onUpdated, processingIds],
  );

  return { processingIds, exitingIds, runExit };
}
